import React, { useEffect, useState } from "react";
import {
  Link
} from "react-router-dom";
import {
  Plus,
  Pencil,
  Trash2,
  Briefcase
} from "lucide-react";
import * as ownerApi from "../../api/payroll/roleSalaryApi";
import * as portalApi from "../../api/payroll/roleSalaryPortalApi";
import {
  getRoles
} from "../../api/roleApi";
import {
  Button,
  IconButton,
  Card,
  CardContent,
  Table,
  Badge,
  EmptyState
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

export default function RoleSalaryList({ isPortal = false }) {
  const api = isPortal ? portalApi : ownerApi;
  const base = isPortal ? "/staff-panel/role-salaries" : "/role-salaries";

  const [list, setList] = useState([]);
  const toast = useToast();

  const [roles, setRoles] = useState([]); // Store roles list for mapping
  const [loading, setLoading] = useState(true);
  const loadData = async () => {
    try {
      setLoading(true);

      // Fetch both role salaries and the master roles list in parallel
      const [salariesRes, rolesRes] = await Promise.all([
        api.getAllRoleSalaries(),
        getRoles().catch(() => []), // Safely fallback if roles fetch fails
      ]);

      setList(Array.isArray(salariesRes) ? salariesRes : salariesRes?.data || []);

      const rolesList = Array.isArray(rolesRes)
        ? rolesRes
        : rolesRes?.data || rolesRes?.roles || [];
      setRoles(rolesList);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPortal]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this role salary?")) return;
    try {
      await api.deleteRoleSalary(id);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Delete failed");
    }
  };

  // Helper function to resolve the Role Name
  const getRoleName = (roleField) => {
    if (!roleField) return "-";

    // Scenario A: If populated by backend as an object
    if (typeof roleField === "object") {
      return roleField.roleName || roleField.name || "-";
    }

    // Scenario B: If backend returned raw ID string, match it against loaded roles
    const foundRole = roles.find((r) => r._id === roleField);
    if (foundRole) {
      return foundRole.roleName || foundRole.name;
    }

    // Fallback if not matched yet
    return roleField;
  };

  // ---- Presentation-only derivations below (no data/logic changes) ----

  const tableRows = list.map((r) => ({ ...r, id: r._id }));

  const columns = [
    {
      key: "role",
      header: "Role",
      render: (r) => <span className="font-medium text-neutral-900 dark:text-neutral-100">{getRoleName(r.roleId)}</span>,
    },
    {
      key: "designation",
      header: "Designation",
      render: (r) => r.designation || "-",
    },
    {
      key: "fixedSalary",
      header: "Fixed Salary",
      align: "right",
      render: (r) => (
        <span className="font-semibold text-neutral-900 dark:text-neutral-100">
          ₹{Number(r.fixedSalary || 0).toLocaleString()}
        </span>
      ),
    },
    {
      key: "effectiveFrom",
      header: "Effective From",
      render: (r) => r.effectiveFrom || "-",
    },
    {
      key: "status",
      header: "Status",
      render: (r) => <Badge variant={r.active ? "success" : "default"}>{r.active ? "Active" : "Inactive"}</Badge>,
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      nowrap: true,
      render: (r) => (
        <div className="flex items-center justify-end gap-1">
          <Link to={`${base}/edit/${r._id}`}>
            <IconButton aria-label="Edit role salary" variant="ghost" size="sm">
              <Pencil className="h-4 w-4" aria-hidden="true" />
            </IconButton>
          </Link>
          <IconButton
            aria-label="Delete role salary"
            variant="ghost"
            size="sm"
            onClick={() => handleDelete(r._id)}
            className="hover:bg-danger-50 hover:text-danger-600"
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </IconButton>
        </div>
      ),
    },
  ];

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 border-b border-neutral-200 dark:border-neutral-800 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-100 sm:text-2xl">
            Role Fixed Salaries
          </h1>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            Default monthly pay configured per role/designation
          </p>
        </div>
        <Link to={`${base}/create`}>
          <Button variant="primary" leftIcon={<Plus className="h-4 w-4" />}>
            Add / Update
          </Button>
        </Link>
      </div>

      

      <Card>
        <CardContent className="p-0">
          <Table
            columns={columns}
            data={tableRows}
            isLoading={loading}
            emptyState={
              <EmptyState
                icon={Briefcase}
                title="No role salaries configured"
                description="Add a fixed salary for a role to get started."
                action={
                  <Link to={`${base}/create`}>
                    <Button variant="primary" size="sm" leftIcon={<Plus className="h-4 w-4" />}>
                      Add / Update
                    </Button>
                  </Link>
                }
              />
            }
          />
        </CardContent>
      </Card>
    </div>
  );
}